import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 3.3 visual preview at /preview/lesson/3.3
 * "The draft step: getting words on the page"
 */

export default function LessonPreviewPage3_3() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 3 · Building with AI · Lesson 3.3
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The draft step: getting words on the page
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Voice · Judgment · Character</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 drafting drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="Two essays. One student. One was written. One was assigned.">
      <p className="mt-6 text-text/90">
        A teacher gets two essays from the same student in the same week. Both are on
        topics the student picks. Both are 800 words. Both are grammatically clean.
      </p>
      <p className="mt-4 text-text/90">
        The first is on basketball. The student plays varsity. The essay has weird
        sentence rhythms, a joke that does not quite land, a specific story about a
        coach yelling at halftime, and one paragraph the teacher reads twice because
        it actually says something the teacher has not heard before.
      </p>
      <p className="mt-4 text-text/90">
        The second is on the French Revolution. The essay is cleaner. Better
        transitions. Bigger words. Every paragraph ends with a tidy summary sentence.
        No jokes. No moments where the writing surprises you. It reads like an
        encyclopedia explaining itself.
      </p>
      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          What the teacher writes on the second essay
        </p>
        <p className="mt-3 text-text/90 italic">
          &ldquo;This is well-written but it does not sound like you. Did you write
          this, or did AI write this for you?&rdquo;
        </p>
      </div>
      <p className="mt-6 text-text/90">
        The student did write it. The student also let AI write most of it. The
        student cannot explain the difference. The grade comes back lower than the
        basketball essay, even though the second one is technically better.
      </p>
      <p className="mt-4 text-text/90">
        The research step is about catching what is not true. The draft step is about
        catching what is not yours.
      </p>
    </Section>
  );
}

/* --- Section 2: From last time --- */
function FromLastTime() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 3.2" title="Three questions on the research step.">
      <p className="mt-6 text-text/90">
        Three questions on research. No looking back.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define primary source in one sentence." />
        <ReviewItem n="02" q="What are the three buckets AI pulls from when it answers a research question?" />
        <ReviewItem n="03" q="What is the one move that catches almost every answer-shaped lie?" />
      </ol>
      <p className="mt-6 text-sm text-muted">
        If those felt fuzzy, run 3.2 again. Research and drafting are the first two
        links in the chain. If research is shaky, drafting is built on sand.
      </p>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The draft, real version" title="What drafting is. What AI takes. How to keep your voice.">
      <Subhead n={1} title="Drafting with AI vs AI drafting for you" />
      <p className="mt-4 text-text/90">
        These look the same on the page. They are not the same. The difference shows
        up in how the work feels to the reader, and in what you can defend on Monday
        if a teacher asks you to explain a paragraph.
      </p>
      <p className="mt-4 text-text/90">
        Drafting with AI: you do the thinking. AI helps you move faster. You decide
        the shape, the order, the point. AI fills in transitions or expands a bullet
        you already wrote. Every sentence is something you would have said anyway.
      </p>
      <p className="mt-4 text-text/90">
        AI drafting for you: AI does the thinking. You ask for a paragraph on a
        topic. AI invents a shape, an order, a point. You read it, you agree with
        most of it, you keep it. Every sentence is something an average writer might
        have said about that topic. None of it is you.
      </p>
      <ModeFrame />

      <Subhead n={2} title="The thing you have to name" className="mt-12" />
      <p className="mt-4 text-text/90">
        There is a name for the part of writing that makes it sound like you and not
        like an average writer. With the name, you can see it. Without the name, AI
        sands it off and you do not notice.
      </p>
      <VocabCard
        term="Voice fingerprint"
        definition="The small habits in your writing that make a paragraph sound like you wrote it: word choices you reach for, sentence rhythms you fall into, jokes you make, things you notice that other people do not, the way you start and end. Other writers have other fingerprints. AI has no fingerprint of its own. AI defaults to an average of every writer it was trained on."
        analogy="Your handwriting and your voice on a voicemail are also fingerprints. Two people can write the same sentence and a friend can still tell who wrote which one. That tell is the fingerprint."
        memoryHook="Specific word choices. Sentence rhythm. What you notice. Where the jokes are."
        why="Teachers can tell. College admissions readers can tell. Friends can tell. AI cannot fake your fingerprint because AI does not know what it is. Only you do. If you let AI draft for you, the fingerprint disappears, and the work stops sounding like you wrote it even when you did."
      />

      <Subhead n={3} title="The three drafting modes" className="mt-12" />
      <p className="mt-4 text-text/90">
        Most students use AI in exactly one mode: type the assignment, paste the
        answer. That is the mode that erases your voice. There are two other modes
        that keep your voice and still get you moving faster.
      </p>
      <ModesGrid />

      <Subhead n={4} title="What AI takes when you let it draft" className="mt-12" />
      <p className="mt-4 text-text/90">
        When AI drafts for you, three things vanish. They vanish quietly. You will
        not notice unless someone points it out. The reader notices first.
      </p>
      <VanishGrid />
    </Section>
  );
}

function ModeFrame() {
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2">
      <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
          AI drafting for you · Wrong
        </p>
        <p className="mt-3 text-text/90">
          You type: write me a 5-paragraph essay on the French Revolution. AI returns
          a 5-paragraph essay on the French Revolution. You read it. You agree. You
          submit it.
        </p>
        <p className="mt-3 text-text/80">
          What you have: writing that is technically correct, technically yours
          because you submitted it, and not yours in any sense that matters. You
          cannot defend a single sentence past &ldquo;AI said it.&rdquo;
        </p>
      </div>
      <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          Drafting with AI · Right
        </p>
        <p className="mt-3 text-text/90">
          You write a messy outline in your own words. You write your own opening
          paragraph. You ask AI to suggest three weak spots in your argument. You
          rewrite two of them. You ask AI to tighten one transition. You read every
          sentence out loud.
        </p>
        <p className="mt-3 text-text/80">
          What you have: writing where every idea is yours, every sentence sounds
          like you, and AI made the work faster, not different.
        </p>
      </div>
    </div>
  );
}

const MODES = [
  {
    n: '01',
    name: 'Blank-page mode',
    accent: 'amber' as const,
    when: 'You are stuck. You have the assignment but no first sentence.',
    use: 'Ask AI for three possible opening moves: a story, a stat, a question. Do not use any of them as-is. Pick the one that feels closest, then rewrite it in your own words.',
    keep: 'You write the actual opening. AI gives you a direction.',
  },
  {
    n: '02',
    name: 'Expander mode',
    accent: 'emerald' as const,
    when: 'You have a bullet or a half-sentence. You know what you want to say. You do not know how to stretch it into a paragraph.',
    use: 'Paste your bullet. Ask AI to write three different paragraph versions. Do not pick one. Steal the structure of the one that feels right, then write the paragraph yourself using your own examples and word choices.',
    keep: 'You wrote the bullet. You wrote the final paragraph. AI showed you a shape.',
  },
  {
    n: '03',
    name: 'Polish mode',
    accent: 'amber' as const,
    when: 'Your draft is done. It is rough. You want it tighter without it sounding generic.',
    use: 'Paste one paragraph at a time. Ask AI: tighten this without changing the voice or the word choices. Review every change. Reject anything that makes the sentence sound less like you. Re-type the changes you accept by hand so they pass through your brain.',
    keep: 'The ideas, the structure, and the voice are all still yours. AI smoothed the edges.',
  },
];

function ModesGrid() {
  return (
    <div className="mt-6 space-y-4">
      {MODES.map((m) => (
        <ModeCard key={m.n} mode={m} />
      ))}
    </div>
  );
}

function ModeCard({ mode }: { mode: (typeof MODES)[number] }) {
  const isEmerald = mode.accent === 'emerald';
  const borderCls = isEmerald ? 'border-emerald-500/40' : 'border-amber/40';
  const bgCls = isEmerald ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
  const labelCls = isEmerald ? 'text-emerald-300' : 'text-amber';
  return (
    <div className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="font-mono text-xs text-muted">{mode.n}</span>
        <h4 className={`text-lg font-semibold ${labelCls}`}>{mode.name}</h4>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Block label="When to use" body={mode.when} />
        <Block label="The move" body={mode.use} />
        <Block label="What stays yours" body={mode.keep} />
      </div>
    </div>
  );
}

const VANISHES = [
  {
    n: '01',
    label: 'Your word choices',
    body: 'AI defaults to the most common word. You might say “wrecked”. AI says “significantly impacted.” Multiply that across every sentence. Your voice disappears one word at a time.',
  },
  {
    n: '02',
    label: 'Your sentence rhythm',
    body: 'You write short. Then long. Then short again. AI writes medium, medium, medium. The page becomes a flat surface with no shape. Teachers feel this even when they cannot point at why.',
  },
  {
    n: '03',
    label: 'What you noticed',
    body: 'The best writing has one thing the reader has not heard before. A small specific observation only you would make. AI cannot make it because AI was not there. If AI drafts, that observation never reaches the page.',
  },
];

function VanishGrid() {
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-3">
      {VANISHES.map((v) => (
        <div key={v.n} className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
            {v.n} · {v.label}
          </p>
          <p
            className="mt-3 text-sm text-text/85"
            dangerouslySetInnerHTML={{ __html: v.body }}
          />
        </div>
      ))}
    </div>
  );
}

/* --- Section 4: Your turn · Drafting drills --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Pick the mode" title="Three situations. Pick how you would use AI in each.">
      <p className="mt-6 text-text/90">
        For each situation, pick the drafting mode that fits. Then check yourself
        against the model verdict. Same answer might look different than yours. The
        reason matters more than the label.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <DraftDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

const MODE_OPTIONS = [
  { key: 'blank', label: 'Blank-page mode', short: 'Blank' },
  { key: 'expander', label: 'Expander mode', short: 'Expander' },
  { key: 'polish', label: 'Polish mode', short: 'Polish' },
  { key: 'none', label: 'Do not use AI here', short: 'No AI' },
];

type DrillSpec = {
  n: string;
  situation: string;
  correct: string;
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    situation:
      'You have a finished draft of a personal statement for a college application. It is 600 words. It sounds like you. You want it sharper without losing your voice.',
    correct: 'polish',
    verdict:
      'Polish mode. The thinking is done. The voice is in place. AI tightens sentence by sentence with strict instructions to preserve your word choices. Anywhere AI changes your voice, reject the change. Re-type the changes you keep by hand so they pass through your brain.',
  },
  {
    n: '02',
    situation:
      'You have a one-line argument for a history essay: “The Marshall Plan worked because it was framed as defense, not charity.” You need a body paragraph that defends that line. You are stuck on shape.',
    correct: 'expander',
    verdict:
      'Expander mode. Your argument is already yours. You are stuck on structure, not substance. Ask AI for two or three paragraph shapes that could defend that line. Take the structure of the one you like. Then write the paragraph yourself using your own examples and your own phrasing. AI showed you the shape. You filled it.',
  },
  {
    n: '03',
    situation:
      'You have to write a 2-page reflection on a basketball game you played in. It is due tomorrow. You have not started. You are not sure how to open it.',
    correct: 'none',
    verdict:
      'Do not use AI here. This is the basketball essay from the cold open. You were there. AI was not. The whole assignment is your fingerprint: what you noticed, what you felt, what surprised you. Blank-page mode might unblock you, but the risk is high that you take an AI opening and the whole reflection loses the thing that makes it good. Start with one sentence about a specific moment from the game. Build from there. The opening can be ugly. It will be yours.',
  },
];

function DraftDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<string | null>(null);
  const correctMeta = MODE_OPTIONS.find((o) => o.key === drill.correct);
  const pickedMeta = MODE_OPTIONS.find((o) => o.key === picked);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The situation
      </p>
      <p
        className="mt-3 text-text/90 italic"
        dangerouslySetInnerHTML={{ __html: drill.situation }}
      />
      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which mode do you use?
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {MODE_OPTIONS.map((opt) => {
            const isPicked = picked === opt.key;
            const isCorrect = opt.key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-3 text-left text-sm text-text/85 transition-colors hover:border-amber';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500/60 bg-emerald-500/[0.08] p-3 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500/60 bg-rose-500/[0.08] p-3 text-left text-sm text-text';
            return (
              <button
                key={opt.key}
                className={cls}
                onClick={() => setPicked(opt.key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option · {opt.short}
                </span>
                <span className="mt-1 block">{opt.label}</span>
              </button>
            );
          })}
        </div>
        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · {pickedMeta?.label}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That is not the mode this situation calls for. See the correct
              answer below.
            </p>
          </div>
        )}
        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The right mode' : 'Correct'} · {correctMeta?.label}
            </p>
            <p className="mt-2 text-sm text-text/90">{drill.verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define voice fingerprint in one sentence." answer="The small habits in your writing (word choices, sentence rhythm, what you notice, where the jokes are) that make a paragraph sound like you wrote it." />
        <QuickCheckItem n="Q2" q="Name the three drafting modes." answer="Blank-page mode (unstuck the opening), Expander mode (turn a bullet into a paragraph), Polish mode (tighten a finished draft without losing voice)." />
        <QuickCheckItem n="Q3" q="What is the difference between drafting with AI and AI drafting for you, in one sentence?" answer="In drafting with AI you do the thinking and AI moves you faster; when AI drafts for you, AI does the thinking and your voice disappears." />
        <QuickCheckItem n="Q4" q="A teacher writes &lsquo;this does not sound like you&rsquo; on your essay. What probably happened?" answer="AI drafted for you. Your word choices, sentence rhythm, and specific observations got replaced with the average of every writer AI was trained on. The teacher cannot point at the line, but they can feel it." />
        <QuickCheckItem n="Q5" q="What is one habit that protects your voice when you use AI to polish?" answer="Re-type the changes you accept by hand. Typing forces every word through your brain. Pasting does not." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. Run it any time you draft with AI.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 3.3 · Lock card · The draft step
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Draft with AI. Do not let AI draft for you. Every sentence has to pass
          through your brain or it stops sounding like you.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <ModeTile n="01" name="Blank-page" body="Stuck on the opening. AI gives you three directions. You write the actual first line." />
          <ModeTile n="02" name="Expander" body="Have a bullet. AI shows you three paragraph shapes. You write the paragraph in your own words." />
          <ModeTile n="03" name="Polish" body="Have a draft. AI tightens sentence by sentence. You reject any change that erases your voice. Re-type what you keep." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The voice check before you submit
          </p>
          <p className="mt-2 text-text/90">
            Read the draft out loud. Stop at any sentence that does not sound like
            something you would say. Rewrite that sentence. If more than three
            sentences fail the check, you did not draft with AI. AI drafted for you.
            Start the section over.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          AI can move the work faster. AI cannot be you. That is your job and nobody
          can outsource it.
        </p>
      </div>
    </Section>
  );
}

function ModeTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 3.4
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The audit step: catching what is wrong before you ship
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Third link in the chain. The two-pass audit that makes the whole chain
            defensible: fact audit first, voice audit second. The move that turns a
            draft into something you would defend if a teacher pulled you aside on
            Monday.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3
      className={`text-xl font-semibold text-text ${className}`}
    >
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function Block({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-text/85">{body}</p>
    </div>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 3.3 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p
            className="text-text/90"
            dangerouslySetInnerHTML={{ __html: q }}
          />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p className="mt-3 border-t border-line pt-3 text-sm text-text/85">
          {answer}
        </p>
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    '3.2 review',
    'Draft, real version',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
